import type { Config } from 'mixpanel-browser';
// The SDK without the session recorder bundled in: the default entry point
// ships rrweb (~225 KB minified) inside the app's vendor bundle for every
// visitor, on every page, before first paint (QA 2026-09-25 #14). This build
// fetches the recorder from `recorder_src` only once a recording starts -
// after consent on Aperio, and still for every Go Oil session. Tests reach
// the same module through package.json jest.moduleNameMapper, so
// jest.mock('mixpanel-browser') keeps working.
import mixpanel from 'mixpanel-browser/dist/mixpanel-with-async-recorder.cjs';
import {
  aperioAnalyticsDeclined,
  aperioAnalyticsGranted,
  aperioFirstTouch,
  isAperioHost,
  noteSignedInRoles,
  trafficSuperProperties,
} from './aperioTracking';
import {
  isSafeDistinctId,
  postal3,
  scrubEventPayload,
  scrubPeoplePayload,
  scrubProperties,
  scrubQueryString,
} from './analyticsScrub';

const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;

// ---------------------------------------------------------------- brand

/**
 * One Mixpanel project holds every brand this portal serves, so that a visitor
 * who starts on a marketing site and finishes here is ONE person rather than
 * two. `brand` is what keeps the boards separable again: the marketing site
 * sends 'aperio', and this portal sends 'aperio' or 'gooil' - nothing else.
 *
 * Audit 2026-09-26 (the Aperio funnel filtered on brand = aperio fell to 0%
 * at its first portal step): the brand used to be the tenant's NAME reduced
 * to letters and digits, which turns a tenant called "Aperio Mobility" into
 * 'aperiomobility', and before the tenant loaded it was the host's first
 * label, which on book.aperiomobility.com ('book') fell back to 'gooil'.
 * It now comes from the tenant's preset / subdomain (name as a last resort)
 * and, before the tenant is known, from the host test the rest of the
 * portal uses for Aperio - and it is set on EVERY event three times over:
 * the super property, the mp.track wrapper, and the SDK's before_send_events
 * hook, which has the final word on every payload (a stale persisted value
 * or an "undefined" included).
 */
export type Brand = 'aperio' | 'gooil';

/** What the tenant record says about the brand (GET api/tenant). */
export interface BrandSource {
  name?: string | null;
  subdomain?: string | null;
  themePreset?: string | null;
}

/** A known brand key, or null for anything else ('undefined' included). */
export function normalizeBrand(value: unknown): Brand | null {
  if (typeof value !== 'string') return null;
  const key = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (key === 'aperio' || key === 'aperiomobility') return 'aperio';
  if (key === 'gooil') return 'gooil';
  return null;
}

/**
 * The brand for a tenant (its record, or just its name), or - with no
 * tenant - for the host this page is served on.
 */
export function resolveBrand(tenant?: string | BrandSource | null): Brand {
  const source: BrandSource = typeof tenant === 'string'
    ? { name: tenant }
    : (tenant || {});
  const preset = String(source.themePreset || '').trim().toLowerCase();
  const subdomain = String(source.subdomain || '').trim().toLowerCase();
  const name = String(source.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (preset === 'aperio' || subdomain === 'aperio' || name.startsWith('aperio')) {
    return 'aperio';
  }
  if (preset || subdomain || name) {
    return 'gooil';
  }
  const host = typeof window !== 'undefined' && window.location
    ? window.location.hostname : '';
  return isAperioHost(host || '') ? 'aperio' : 'gooil';
}

/** The brand this page reports under; set from the tenant by bootTheme. */
let activeBrand: Brand | undefined;

export function analyticsBrand(): Brand {
  return activeBrand || resolveBrand();
}

/**
 * `before_send_events` hook: the personal-data scrubber, then `brand`,
 * whatever the payload held before.
 */
export function brandEventPayload<T>(data: T): T {
  if (!data || typeof data !== 'object') return data;
  const payload = data as unknown as { properties?: Record<string, unknown> };
  if (!payload.properties || typeof payload.properties !== 'object') {
    return data;
  }
  return {
    ...data,
    properties: { ...payload.properties, brand: analyticsBrand() },
  };
}

export function beforeSendEvents<T>(data: T): T {
  return brandEventPayload(scrubEventPayload(data));
}

// ---------------------------------------------------------- persistence

const APERIO_SITE_HOST = /(^|\.)aperiomobility\.com$/i;
const APERIO_COOKIE_DOMAIN = '.aperiomobility.com';

/** The SDK's own persistence entry (cookie or localStorage key). */
export const mixpanelStoreName = (token: string): string => `mp_${token}_mixpanel`;

/**
 * Where Mixpanel keeps the visitor's identity.
 *
 * - On aperiomobility.com hosts (book.aperiomobility.com), once the visitor
 *   has accepted analytics on the marketing site (`apr_consent` a1, no GPC):
 *   a first-party cookie on .aperiomobility.com - the same cookie the site
 *   writes - so the site and the portal share one distinct_id. The SDK moves
 *   an older localStorage record into the cookie on the first load, as long
 *   as no cookie exists yet (MixpanelPersistence.upgrade), so a returning
 *   visitor keeps their id; with the site's cookie already there, the site's
 *   id wins and the old portal record's other properties are merged in.
 * - Everywhere else - Go Oil, aperio.gooil.app (a cross-subdomain cookie
 *   there would be shared with gooil.app), and an Aperio visitor who has made
 *   no choice on the site - localStorage, exactly as before.
 */
export function mixpanelPersistence(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
  protocol: string = typeof window !== 'undefined' ? window.location.protocol : 'https:',
): Partial<Config> {
  if (APERIO_SITE_HOST.test(host) && aperioAnalyticsGranted()) {
    return {
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      secure_cookie: protocol === 'https:',
    };
  }
  return { persistence: 'localStorage' };
}

/**
 * Deletes the shared Mixpanel cookie (both the .aperiomobility.com one and a
 * host-only one) - for a visitor who declined analytics, on an Aperio host.
 */
export function clearMixpanelCookie(
  token: string,
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): void {
  if (typeof document === 'undefined' || !token) return;
  const name = mixpanelStoreName(token);
  if (!document.cookie.split(';').some((c) => c.trim().startsWith(`${name}=`))) {
    return;
  }
  const tail = '=; Path=/; Max-Age=0; SameSite=Lax';
  try {
    document.cookie = `${name}${tail}`;
    if (APERIO_SITE_HOST.test(host)) {
      document.cookie = `${name}${tail}; Domain=${APERIO_COOKIE_DOMAIN}`;
    }
  } catch (e) {
    // cookies blocked: nothing was stored either
  }
}

/**
 * The page URL carries something the scrubber would remove (the hand-off's
 * street / city / postal / lat / lng, an e-mail). Session replay uploads
 * send the raw `location.href` as `$current_url` / `replay_start_url` on
 * api-js.mixpanel.com/record, which the before_send hooks never see (QA
 * 2026-09-25, #23), so on such a URL the recording waits.
 */
export function urlCarriesPersonalData(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  return !!search && search !== '?' && scrubQueryString(search) !== search;
}

/**
 * Text that is masked in an Aperio session replay. Every input is always
 * masked by the SDK (maskAllInputs is hard-wired), so what a visitor types
 * in name, e-mail, phone, address, card or password boxes never reaches a
 * replay. Page text is shown (owner 2026-10-28: headings, services, prices,
 * buttons, plan cards, vehicle choices and errors must be readable), except
 * where it repeats those personal details: Google's address suggestions
 * (`.pac-container`) and anything under `[data-mp-mask]` (the summary's
 * street, the confirmation's address / e-mail, the signed-in account and
 * staff screens).
 */
export const REPLAY_MASK_SELECTOR = '[data-mp-mask], .pac-container';

/**
 * Session replay on an Aperio host (QA 2026-09-25, #5 / #6): the booking
 * pages carry the visitor's address, name, e-mail and phone, so they are
 * recorded ONLY after an analytics accept on the site (`apr_consent` a1, no
 * GPC; aperioAnalyticsGranted) - never on a direct landing with no decision,
 * never after a decline - with the personal text masked
 * (REPLAY_MASK_SELECTOR) and the rest of the page readable. Heatmap clicks
 * still work. Every other host (Go Oil) keeps exactly the old options.
 */
export function aperioReplayOptions(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): Partial<Config> | null {
  if (!isAperioHost(host)) return null;
  if (aperioAnalyticsGranted()) {
    // A URL that still carries the hand-off address: start the recording
    // only once the app has stripped it (startReplayWhenUrlIsClean).
    return {
      record_mask_text_selector: REPLAY_MASK_SELECTOR,
      ...(urlCarriesPersonalData(search) && { record_sessions_percent: 0 }),
    } as Partial<Config>;
  }
  return { record_sessions_percent: 0, record_heatmap_data: false } as Partial<Config>;
}

/**
 * Calls `start` once the URL no longer carries personal data (the booking
 * flow strips the hand-off query with history.replaceState), checking every
 * half second for up to a minute; after that this page view is not recorded.
 */
export function startReplayWhenUrlIsClean(start: () => void, tries = 120, everyMs = 500): void {
  const tick = (left: number) => {
    if (!urlCarriesPersonalData()) {
      try {
        start();
      } catch (e) {
        // replay is best effort
      }
      return;
    }
    if (left > 0) setTimeout(() => tick(left - 1), everyMs);
  };
  tick(tries);
}

/** The init options (tests read them through initAnalytics' mock). */
export function mixpanelConfig(
  aperioDeclined: boolean,
  host?: string,
): Partial<Config> {
  return {
    // Last line of defence against personal data (util/analyticsScrub),
    // and the guarantee that every event carries the brand: every event
    // and people update goes through these right before it leaves, super
    // properties and the SDK's own $current_url / $referrer included.
    hooks: {
      before_send_events: beforeSendEvents,
      before_send_people: scrubPeoplePayload,
    },
    record_sessions_percent: 100,
    record_heatmap_data: true,
    record_mask_text_selector: '',
    record_block_selector: '',
    track_pageview: false,
    ...mixpanelPersistence(host),
    debug: true,
    ignore_dnt: true,
    ...aperioReplayOptions(host),
    ...(aperioDeclined && {
      record_sessions_percent: 0,
      record_heatmap_data: false,
      opt_out_tracking_by_default: true,
    }),
  } as Partial<Config>;
}

/**
 * Aperio hosts with no analytics accept on file: Mixpanel is never
 * initialised and every `mp` call is a no-op - nothing loaded, stored or
 * sent until the visitor accepts (the booking site's cookie bar: "nothing
 * is measured until you accept"; owner, 2026-10-21). Our own first-party
 * server logging (booking log, drafts) is not a vendor and is unaffected.
 */
let held = false;

/** True while Mixpanel is held back for want of consent (Aperio hosts). */
export function analyticsHeld(): boolean {
  return held;
}

export function initAnalytics(
  options: { host?: string; token?: string | null } = {},
) {
  const token = options.token === undefined ? mixpanelToken : options.token;
  const aperio = isAperioHost(options.host);
  // Aperio hosts: Mixpanel only after an analytics accept on the site
  // (`apr_consent` a1, explicit or the site's implied grant, and no Global
  // Privacy Control). No choice, a decline or GPC: not initialised at all,
  // and a decline deletes the identity cookie shared with the site. Every
  // other host, Go Oil included, initialises exactly as before.
  held = aperio && !aperioAnalyticsGranted();
  if (held) {
    if (token && aperioAnalyticsDeclined()) clearMixpanelCookie(token, options.host);
    return;
  }
  if (!token) {
    console.warn('Mixpanel token missing');
    return;
  }

  // `hooks` is a real init option that the bundled typings do not list.
  mixpanel.init(token, mixpanelConfig(false, options.host));
  if (aperio) {
    try {
      if (mixpanel.has_opted_out_tracking()) {
        // An earlier decline was withdrawn on the site: forget it
        // (without sending anything) so tracking resumes as before.
        mixpanel.clear_opt_in_out_tracking();
      }
      // Consented, but the page URL still holds the typed address: the
      // recording starts once the app has removed it (QA #23).
      if (urlCarriesPersonalData()) {
        startReplayWhenUrlIsClean(() => {
          const m = mixpanel as unknown as Record<string, unknown>;
          const start = m.start_session_recording;
          if (typeof start === 'function') start.call(mixpanel);
        });
      }
    } catch (e) {
      // consent handling is best effort; never break the booking flow
    }
  }
  // Provisional brand from the host; bootTheme sets it from the tenant as
  // soon as /api/tenant answers.
  mixpanel.register({ brand: analyticsBrand() });
}

/**
 * The visitor declined on the portal's own consent bar after an implied
 * start (components/ConsentBar): stop Mixpanel for the rest of the page and
 * delete the identity cookie shared with the site. The next page load reads
 * the `apr_consent` decline and never initialises it.
 */
export function stopAnalytics(
  options: { host?: string; token?: string | null } = {},
): void {
  const token = options.token === undefined ? mixpanelToken : options.token;
  if (!held) {
    try {
      mixpanel.opt_out_tracking();
    } catch (e) {
      // not initialised: nothing was sent
    }
  }
  held = true;
  if (token) clearMixpanelCookie(token, options.host);
}

/**
 * Values that must never be stored: undefined, null, empty strings and the
 * strings "undefined" / "null" (what `String(undefined)` would have sent).
 */
export function compactProperties(
  props?: Record<string, unknown> | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(props || {}).forEach((key) => {
    const value = (props as Record<string, unknown>)[key];
    if (value === undefined || value === null) return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;
    if (typeof value === 'string') {
      const text = value.trim();
      if (!text || text === 'undefined' || text === 'null') return;
    }
    if (Array.isArray(value) && !value.length) return;
    out[key] = value;
  });
  return out;
}

/** Super properties that hold the campaign (portal registerAttribution). */
export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'ndclid',
  // OpenAI (ChatGPT) Ads' click reference (the site registers it too).
  'oppref',
];

/** Super properties that mark our own traffic (registerTrafficType). */
export const TRAFFIC_KEYS = ['internal', 'traffic_type'];

export const mp = {
  // Like identify() and register() below: a build with no Mixpanel token (local
  // dev, a preview deploy) must still book a car. mixpanel-browser throws from
  // track() when init() never ran, and that throw used to come out of a render
  // effect on the Location step - the first screen of the booking flow, and the
  // one the marketing site hands visitors to.
  //
  // Every property goes through the personal-data scrubber first
  // (util/analyticsScrub): no street, full postal code, email, phone, name,
  // VIN, plate or coordinates reaches Mixpanel, whatever a caller passes.
  // `brand` is set here, on every event, whatever the caller passed.
  track: (name: string, props?: Record<string, any>) => {
    if (held) return;
    try {
      mixpanel.track(name, { ...scrubProperties(props), brand: analyticsBrand() });
    } catch (e) {
      // analytics not initialised - the event is lost, the flow is not
    }
  },
  time_event: (name: string) => {
    if (held) return;
    try {
      mixpanel.time_event(name);
    } catch (e) {
      // analytics not initialised
    }
  },
  // identify() is called from the marketing-site hand-off, which happens on a
  // route that also renders with no Mixpanel token configured (local dev, a
  // preview build). It must never throw there: a missing analytics token is
  // not a reason to break the booking flow. An id that looks like an email,
  // a phone number or a VIN is refused: the distinct id shows in every report.
  identify: (id: string) => {
    if (held || !isSafeDistinctId(id)) return;
    try {
      mixpanel.identify(id);
    } catch (e) {
      // analytics not initialised - identity stitching is best effort
    }
  },
  alias: (id: string) => {
    if (held || !isSafeDistinctId(id)) return;
    try {
      mixpanel.alias(id);
    } catch (e) {
      // analytics not initialised
    }
  },
  // People profiles hold non-personal properties only (see identifyUser):
  // never $name, $email or $phone. Empty values are dropped, so a profile
  // never stores "undefined".
  peopleSet: (props: Record<string, any>) => {
    if (held) return;
    const clean = compactProperties(scrubProperties(props));
    if (!Object.keys(clean).length) return;
    try {
      mixpanel.people.set(clean);
    } catch (e) {
      // analytics not initialised
    }
  },
  peopleSetOnce: (props: Record<string, any>) => {
    if (held) return;
    const clean = compactProperties(scrubProperties(props));
    if (!Object.keys(clean).length) return;
    try {
      mixpanel.people.set_once(clean);
    } catch (e) {
      // analytics not initialised
    }
  },
  // Super properties: attached to every subsequent event. Used for campaign /
  // referral attribution, so it must never throw when Mixpanel is not
  // configured (no token in the environment). A `brand` must be one of the
  // two brand keys; anything else is dropped.
  register: (props: Record<string, any>) => {
    const clean = compactProperties(scrubProperties(props));
    if ('brand' in (props || {})) {
      const brand = normalizeBrand(clean.brand);
      if (brand) {
        activeBrand = brand;
        clean.brand = brand;
      } else {
        delete clean.brand;
      }
    }
    if (held || !Object.keys(clean).length) return;
    try {
      mixpanel.register(clean);
    } catch (e) {
      // analytics not initialised - attribution is best effort
    }
  },
  unregister: (key: string) => {
    if (held) return;
    try {
      mixpanel.unregister(key);
    } catch (e) {
      // analytics not initialised
    }
  },
  // Logout: a new anonymous device id and no super properties, so the next
  // person on this browser is not merged into the last one. The brand goes
  // straight back (and the send hook sets it regardless): no event is ever
  // sent without one.
  reset: () => {
    if (held) return;
    try {
      mixpanel.reset();
      mixpanel.register({ brand: analyticsBrand(), ...trafficSuperProperties() });
    } catch (e) {
      // analytics not initialised
    }
  },
  optIn: () => { if (!held) mixpanel.opt_in_tracking(); },
  optOut: () => { if (!held) mixpanel.opt_out_tracking(); },
};

/**
 * The tenant is known (bootTheme): this is the brand from now on, on the
 * super property and on every payload.
 */
export function setAnalyticsBrand(tenant?: string | BrandSource | null): Brand {
  const brand = resolveBrand(tenant);
  activeBrand = brand;
  mp.register({ brand });
  return brand;
}

/**
 * The campaign as super properties (Aperio: util/aperioTracking
 * attributionSuperProperties, from the landing URL and the shared `apr_attr`
 * cookie). A campaign key the current touch does not carry is unregistered,
 * so an older campaign's utm_medium never rides along with a newer
 * campaign's utm_source. Nothing happens without a touch.
 */
export function registerAttribution(props: Record<string, string>): void {
  const clean = compactProperties(props) as Record<string, string>;
  if (!ATTRIBUTION_KEYS.some((k) => clean[k])) return;
  mp.register(clean);
  ATTRIBUTION_KEYS.forEach((key) => {
    if (!clean[key]) mp.unregister(key);
    if (!clean[`initial_${key}`]) mp.unregister(`initial_${key}`);
  });
}

/**
 * Our own traffic (Aperio: util/aperioTracking trafficSuperProperties, the
 * QA flag or the internal flag): `internal: true` + `traffic_type:
 * 'internal'` on every event, so boards can leave it out; otherwise both
 * are unregistered, so a flag cleared with `?qa=0` / `?apr_internal=0`
 * does not linger in the persisted (shared) super properties.
 */
export function registerTrafficType(): void {
  const props = trafficSuperProperties();
  if (Object.keys(props).length) {
    mp.register(props);
  } else {
    TRAFFIC_KEYS.forEach((key) => mp.unregister(key));
  }
}

// ------------------------------------------------------------- identity

/**
 * Roles that work for a franchise (tech app, manager panel). Their profiles
 * carry `franchise_id`; a customer's never does.
 */
const STAFF_ROLES = ['manager', 'technician', 'sales', 'tester'];
/** Which role a multi-role account is filed under, most specific first. */
const ROLE_PRIORITY = [
  'admin',
  'manager',
  'technician',
  'sales',
  'tester',
  'fleet_manager',
  'partner_shop',
  'customer',
];

export function primaryRole(roles?: unknown): string {
  const list = Array.isArray(roles)
    ? roles.filter((r): r is string => typeof r === 'string' && !!r)
    : [];
  return ROLE_PRIORITY.find((r) => list.includes(r)) || list[0] || 'customer';
}

/** What identifyUser needs from the logged-in user (auth.currentUser). */
export interface AnalyticsUser {
  id?: number | string | null;
  roles?: string[] | null;
  franchiseId?: number | null;
  token?: string | null;
  email?: string | null;
}

/** An investor demo login (server modules/demo/demo-identity.ts). */
export function isDemoLogin(email: unknown): boolean {
  return typeof email === 'string'
    && /^demo-[a-z0-9._+-]*@aperiomobility\.com$/i.test(email.trim());
}

/** The database user id as a distinct id, or null when it is not one. */
export function userDistinctId(id: unknown): string | null {
  if (typeof id !== 'number' && typeof id !== 'string') return null;
  const value = String(id).trim();
  return /^[1-9]\d*$/.test(value) ? value : null;
}

/**
 * The user id (`sub`) inside the portal's own access token, for the one
 * login that does not hand the id over (password reset). Read, not
 * verified: it only picks the analytics id, it never grants anything.
 */
export function userIdFromToken(token: unknown): string | null {
  if (typeof token !== 'string') return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return userDistinctId(JSON.parse(atob(padded))?.sub);
  } catch (e) {
    return null;
  }
}

let identifiedAs: string | null = null;
let lastProfile = '';
let firstTouchSentFor: string | null = null;

/** Mixpanel date-time: ISO 8601 without milliseconds or zone (UTC). */
export const mixpanelDate = (date: Date): string => date.toISOString().slice(0, 19);

/**
 * The language the visitor uses: the marketing-site hand-off (`lang=`, kept
 * in sessionStorage under `bookingLang` by usePrefilledAddress), else the
 * document's, else the browser's. en / fr / es, or nothing.
 */
export function preferredLanguage(): string | undefined {
  const pick = (value?: string | null) => {
    const code = String(value || '').trim().slice(0, 2).toLowerCase();
    return ['en', 'fr', 'es'].includes(code) ? code : undefined;
  };
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem('bookingLang');
  } catch (e) {
    stored = null;
  }
  return pick(stored)
    || pick(typeof document !== 'undefined' ? document.documentElement.lang : '')
    || pick(typeof navigator !== 'undefined' ? navigator.language : '');
}

/**
 * What a caller may add to the profile when it knows it (the Schedule step
 * knows the booking's address). Non-personal only: the city and province,
 * the FIRST THREE characters of the postal code, counts and the plan.
 */
export interface ProfileExtras {
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  vehicleCount?: number | null;
  planStatus?: 'none' | 'active' | 'cancelled' | null;
  planName?: string | null;
}

function extrasProfile(extra?: ProfileExtras | null): Record<string, unknown> {
  if (!extra) return {};
  const vehicles = Number(extra.vehicleCount);
  return compactProperties({
    city: extra.city,
    province: extra.province,
    postal3: extra.postalCode ? postal3(extra.postalCode) : undefined,
    vehicle_count: Number.isFinite(vehicles) && vehicles >= 0 && extra.vehicleCount != null
      ? vehicles : undefined,
    plan_status: extra.planStatus,
    plan_name: extra.planName,
  });
}

/**
 * First touch, set once on the profile: the Aperio `apr_attr` first touch
 * (util/aperioTracking, consent permitting), else the `initial_*` super
 * properties already registered on this browser. Landing page = path only.
 */
function firstTouchProfile(): Record<string, unknown> {
  const fromAttr = aperioFirstTouch();
  const registered = (key: string): unknown => {
    try {
      return mixpanel.get_property(key);
    } catch (e) {
      return undefined;
    }
  };
  return compactProperties({
    first_utm_source: fromAttr.utm_source ?? registered('initial_utm_source'),
    first_utm_medium: fromAttr.utm_medium ?? registered('initial_utm_medium'),
    first_utm_campaign: fromAttr.utm_campaign ?? registered('initial_utm_campaign'),
    first_landing_page: fromAttr.landing_page,
  });
}

/**
 * Ties this browser's events to the user's database id - Mixpanel's
 * simplified ID merge, identifying with the stable user id and nothing
 * else - and sets the non-personal profile properties:
 *
 * - `$set`: `role`, `roles`, `brand`, `preferred_language`, `franchise_id`
 *   (staff only), and - when the caller knows them (a booking) - `city`,
 *   `province`, `postal3`, `vehicle_count`, `plan_status`, `plan_name`.
 *   The server keeps `vehicle_count` / `plan_*` / the booking and revenue
 *   totals up to date on its own (server/src/modules/mixpanel).
 * - `$set_once`: `first_utm_source` / `first_utm_medium` /
 *   `first_utm_campaign` / `first_landing_page` (path only).
 *
 * Never `$name`, `$email`, a phone number or a street. Empty values are
 * dropped (mp.peopleSet), so "undefined" is never stored.
 *
 * Called on login, sign-up, app load with a saved session
 * (app/identityAnalyticsListener, index.tsx) and after a booking
 * (identifyBooker). Safe to call repeatedly: identify() and the profile
 * update only go out when something changed.
 *
 * @returns true when the user was identified
 */
export function identifyUser(
  user?: AnalyticsUser | null,
  brand?: string,
  extra?: ProfileExtras | null,
): boolean {
  // An investor demo login is never tracked: no Mixpanel, no replay, no ad
  // tags (the device is treated as ours from here on).
  if (isDemoLogin(user?.email)) {
    noteSignedInRoles(['tester']);
    mp.optOut();
    return false;
  }
  // A staff / admin account on Aperio: this device is ours (internal
  // traffic: Mixpanel internal=true, GA4 traffic_type, no ad tags).
  if (noteSignedInRoles(user?.roles)) registerTrafficType();
  const distinctId = userDistinctId(user?.id) || userIdFromToken(user?.token);
  if (!distinctId) return false;
  if (identifiedAs !== distinctId) {
    mp.identify(distinctId);
    identifiedAs = distinctId;
    lastProfile = '';
  }
  const roles = Array.isArray(user?.roles)
    ? (user?.roles || []).filter((r) => typeof r === 'string')
    : [];
  const isStaff = roles.some((r) => STAFF_ROLES.includes(r));
  const franchiseId = Number(user?.franchiseId);
  const profile: Record<string, unknown> = {
    role: primaryRole(roles),
    brand: normalizeBrand(brand) || analyticsBrand(),
  };
  if (roles.length) profile.roles = roles;
  if (isStaff && Number.isFinite(franchiseId) && franchiseId > 0) {
    profile.franchise_id = franchiseId;
  }
  const language = preferredLanguage();
  if (language) profile.preferred_language = language;
  Object.assign(profile, extrasProfile(extra));
  const key = JSON.stringify(profile);
  if (key !== lastProfile) {
    mp.peopleSet(profile);
    lastProfile = key;
  }
  if (firstTouchSentFor !== distinctId) {
    firstTouchSentFor = distinctId;
    mp.peopleSetOnce(firstTouchProfile());
  }
  return true;
}

/**
 * Right after a booking is created: identify the work order's user (a guest
 * booker included), with the booking's city / province / postal3, and stamp
 * `first_booking_at` once. Not for staff booking on a customer's behalf -
 * that would turn the technician into the customer.
 */
export function identifyBooker(
  userId: unknown,
  brand?: string,
  bookedAt: Date = new Date(),
  extra?: ProfileExtras | null,
): boolean {
  if (!identifyUser({ id: userId as number }, brand, extra)) return false;
  mp.peopleSetOnce({ first_booking_at: bookedAt.toISOString() });
  return true;
}

/** An account was just created on the sign-up page: `signup_date`, once. */
export function markSignup(at: Date = new Date()): void {
  mp.peopleSetOnce({ signup_date: mixpanelDate(at) });
}

/** Logout: forget the user on this browser (mixpanel.reset()). */
export function resetAnalyticsIdentity(): void {
  identifiedAs = null;
  lastProfile = '';
  firstTouchSentFor = null;
  mp.reset();
}

/** Tests only: forget what this module remembers between calls. */
export function resetIdentityStateForTests(): void {
  identifiedAs = null;
  lastProfile = '';
  firstTouchSentFor = null;
  activeBrand = undefined;
}

/**
 * mixpanel-browser prefixes an anonymous distinct id with this. Such an id is
 * a DEVICE id, not a user id: identify() refuses it ("distinct_id cannot have
 * $device: prefix") and the cross-site stitch it was meant for never happens.
 */
export const MIXPANEL_DEVICE_PREFIX = '$device:';

/** Super property the marketing site's device id is carried on. */
export const SITE_DEVICE_ID_PROP = 'site_device_id';

/**
 * Adopt the identity the marketing site handed over on the booking URL.
 *
 * The site sends its Mixpanel distinct id as `mp_id`. Until the visitor logs
 * in, that id is an anonymous device id (`$device:<uuid>` from the SDK, or
 * the bare uuid plus `mp_device=1` from a site that strips the prefix
 * itself). A device id must never go through identify(): the SDK throws and
 * nothing is stitched. It becomes the `site_device_id` super property on
 * every event this session sends instead, which is what lets the two halves
 * of the funnel be joined - and the SDK's own ID merge takes over the moment
 * a real user id is identified later (login / booking).
 *
 * Only an id that is neither flagged nor prefixed as a device id is a user
 * id, and that one IS identified.
 *
 * Returns what was done, for tests and for the caller's own tracking.
 */
export function adoptHandoffIdentity(
  id: string | null | undefined,
  isDeviceId = false,
): 'identified' | 'registered' | 'ignored' {
  const raw = (id || '').trim();
  if (!raw) {
    return 'ignored';
  }
  const prefixed = raw.startsWith(MIXPANEL_DEVICE_PREFIX);
  const bare = prefixed ? raw.slice(MIXPANEL_DEVICE_PREFIX.length) : raw;
  if (!bare) {
    return 'ignored';
  }
  if (prefixed || isDeviceId) {
    mp.register({ [SITE_DEVICE_ID_PROP]: bare });
    return 'registered';
  }
  mp.identify(bare);
  return 'identified';
}

export function getDistinctId(): string | undefined {
  if (held) return undefined;
  try {
    return mixpanel.get_distinct_id();
  } catch {
    return undefined;
  }
}

/**
 * The anonymous device id Mixpanel keeps for this browser, for stitching a
 * server-side record (a coverage check, a booking draft) to the analytics
 * session. Falls back to the distinct id, and to nothing when analytics are
 * not initialised or were declined.
 */
export function getDeviceId(): string | undefined {
  if (held) return undefined;
  try {
    const device = mixpanel.get_property('$device_id');
    if (device) return String(device);
  } catch {
    // analytics not initialised
  }
  return getDistinctId();
}
